import React, { useEffect, useState } from 'react'
import { t } from 'i18next'
import { useDispatch, useSelector } from 'react-redux'
import { addAmounts } from 'redux/actions/cotizacionActions'
import { FormatMoney, getMarginTotal } from 'Utils/functions'
import { VAPermissions } from 'Utils/constants'
import { FooterTextValue } from 'Views/VentaPorProducto/components/FooterTextValue'
import useValidatePermission from 'Hooks/useValidatePermission'

/**
 * Hook personalizado para obtener datos de tabla
 * @param {Object} options - Opciones de configuración
 * @param {Array<string>} options.omitColumns - Array con los nombres de las columnas a omitir
 * @param {boolean} options.isShoppingCart - Indica si se usará para mostrar columnas de carrito de compras
 * @returns {Object} - Objeto con las columnas configuradas
 */
export const useTableData = (options = {}) => {
  const { omitColumns = [], isShoppingCart = false } = options
  const dispatch = useDispatch()
  const seeCostProd = useValidatePermission(VAPermissions.seeCostProd)
  const seeMargenProd = useValidatePermission(VAPermissions.seeMargenProd)
  const cart = useSelector(state => state.cart.cart)
  const [cartColumns, setCartColumns] = useState([])

  const getDataCart = () => {
    let subtotal = 0
    let DiscountAmount = 0
    let Cost = 0
    let totalTax = 0
    let Quantity = 0
    let totalVisitDiscount = 0

    cart?.length &&
      cart.forEach(item => {
        const additionalDiscount = item.VisitDiscountAmount || 0
        const itemNetAmount = item.Price * item.Quantity - additionalDiscount
        const itemTax =
          additionalDiscount > 0
            ? itemNetAmount * item.TaxCategoryValue
            : item.Tax
        totalTax += itemTax
        subtotal += item.Price * item.Quantity
        totalVisitDiscount += additionalDiscount
        DiscountAmount += item.DiscountAmount + additionalDiscount
        Cost += item.Cost * item.Quantity
        Quantity += item.Quantity
      })

    const totalMargin = getMarginTotal(subtotal, Cost)
    const itbms = totalTax
    const total =
      Number(
        FormatMoney(subtotal - totalVisitDiscount, true, 2).replace(/,/g, '')
      ) + Number(FormatMoney(totalTax, true, 2).replace(/,/g, ''))

    dispatch(
      addAmounts({
        Quantity,
        Amount: FormatMoney(subtotal, false, 6),
        Tax: FormatMoney(itbms, false, 6),
        AmountWithTax: FormatMoney(total, false, 6),
      })
    )

    return {
      subtotal,
      DiscountAmount,
      itbms,
      total,
      margin: totalMargin || 0,
    }
  }

  const getCartColumns = () => {
    const cartInfo = getDataCart()
    let footerValues =
      seeCostProd && seeMargenProd
        ? {
            cantidad: {
              value: cartInfo.subtotal,
              key: 'SELL_BY_PROD_T.FOOTER_CATALOG.SUBTOTAL_TYPE',
            },
            precio: {
              value: cartInfo.subtotal,
              key: 'SELL_BY_PROD_T.FOOTER_CATALOG.SUBTOTAL_TYPE',
            },
            total: {
              value: cartInfo?.DiscountAmount,
              key: 'SELL_BY_PROD_T.FOOTER_CATALOG.DISCOUNT_TYPE',
            },
            costo: {
              value: cartInfo?.itbms || null,
              key: 'SELL_BY_PROD_T.FOOTER_CATALOG.ITBMS_TYPE',
            },
            acciones: {
              value: cartInfo?.margin.toFixed(2) || null,
              key: 'SELL_BY_PROD_T.FOOTER_CATALOG.MARGIN_TYPE',
            },
            margen: {
              value: cartInfo?.total || null,
              key: 'SELL_BY_PROD_T.FOOTER_CATALOG.TOTAL_TYPE',
            },
          }
        : seeCostProd && !seeMargenProd
        ? {
            precio: {
              value: cartInfo.subtotal,
              key: 'SELL_BY_PROD_T.FOOTER_CATALOG.SUBTOTAL_TYPE',
            },
            total: {
              value: cartInfo?.DiscountAmount,
              key: 'SELL_BY_PROD_T.FOOTER_CATALOG.DISCOUNT_TYPE',
            },
            costo: {
              value: cartInfo?.itbms || null,
              key: 'SELL_BY_PROD_T.FOOTER_CATALOG.ITBMS_TYPE',
            },
            acciones: {
              value: cartInfo?.total || null,
              key: 'SELL_BY_PROD_T.FOOTER_CATALOG.TOTAL_TYPE',
            },
          }
        : !seeCostProd && seeMargenProd
        ? {
            cantidad: {
              value: cartInfo.subtotal,
              key: 'SELL_BY_PROD_T.FOOTER_CATALOG.SUBTOTAL_TYPE',
            },
            precio: {
              value: cartInfo?.DiscountAmount,
              key: 'SELL_BY_PROD_T.FOOTER_CATALOG.DISCOUNT_TYPE',
            },
            total: {
              value: cartInfo?.itbms || null,
              key: 'SELL_BY_PROD_T.FOOTER_CATALOG.ITBMS_TYPE',
            },
            margen: {
              value: cartInfo?.total || null,
              key: 'SELL_BY_PROD_T.FOOTER_CATALOG.TOTAL_TYPE',
            },
            acciones: {
              value: cartInfo?.margin.toFixed(2) || null,
              key: 'SELL_BY_PROD_T.FOOTER_CATALOG.MARGIN_TYPE',
            },
          }
        : {
            cantidad: {
              value: cartInfo.subtotal,
              key: 'SELL_BY_PROD_T.FOOTER_CATALOG.SUBTOTAL_TYPE',
            },
            precio: {
              value: cartInfo?.DiscountAmount,
              key: 'SELL_BY_PROD_T.FOOTER_CATALOG.DISCOUNT_TYPE',
            },
            total: {
              value: cartInfo?.itbms || null,
              key: 'SELL_BY_PROD_T.FOOTER_CATALOG.ITBMS_TYPE',
            },
            acciones: {
              value: cartInfo?.total || null,
              key: 'SELL_BY_PROD_T.FOOTER_CATALOG.TOTAL_TYPE',
            },
          }
    // Definir todas las posibles columnas
    if (isShoppingCart) {
      footerValues = {
        cantidad: {
          value: cartInfo.subtotal,
          key: 'SELL_BY_PROD_T.FOOTER_CATALOG.SUBTOTAL_TYPE',
        },
        precio: {
          value: cartInfo.DiscountAmount,
          key: 'SELL_BY_PROD_T.FOOTER_CATALOG.DISCOUNT_TYPE',
        },
        total: {
          value: cartInfo.itbms || null,
          key: 'SELL_BY_PROD_T.FOOTER_CATALOG.ITBMS_TYPE',
        },
        margen: {
          value: cartInfo.total || null,
          key: 'SELL_BY_PROD_T.FOOTER_CATALOG.TOTAL_TYPE',
        },
        acciones: {
          value: cartInfo.margin.toFixed(2) || null,
          key: 'SELL_BY_PROD_T.FOOTER_CATALOG.MARGIN_TYPE',
        },
      }
    }

    const allColumns = [
      {
        Header: t('SELL_BY_PROD_T.CODE_LABEL'),
        accessor: 'codigo',
      },
      {
        Header: t('SELL_BY_PROD_T.IMAGE'),
        accessor: 'imagen',
      },
      {
        Header: t('SELL_BY_PROD_T.DESCRIPTION_LABEL'),
        accessor: 'descripcion',
      },
      {
        Header: t('SELL_BY_PROD_T.QUANTITY_LABEL'),
        accessor: 'cantidad',
        Footer:
          (!seeCostProd || isShoppingCart) && footerValues.cantidad ? (
            <FooterTextValue
              value={footerValues.cantidad.value}
              type={t(footerValues.cantidad.key)}
              rounded
            />
          ) : (
            <></>
          ),
      },
      {
        Header: t('SELL_BY_PROD_T.PRICE_LABEL'),
        accessor: 'precio',
        Footer: footerValues.precio ? (
          <FooterTextValue
            value={footerValues.precio.value}
            type={t(footerValues.precio.key)}
            rounded
          />
        ) : (
          <></>
        ),
      },
      {
        Header: t('SELL_BY_PROD_T.TOTAL_LABEL'),
        accessor: 'total',
        Footer: footerValues.total ? (
          <FooterTextValue
            value={footerValues.total.value}
            type={t(footerValues.total.key)}
            rounded
          />
        ) : (
          <></>
        ),
      },
      {
        Header: t('SELL_BY_PROD_T.COST_LABEL'),
        accessor: 'costo',
        Footer:
          seeCostProd && footerValues.costo ? (
            <FooterTextValue
              value={footerValues.costo.value}
              type={t(footerValues.costo.key)}
            />
          ) : (
            <></>
          ),
      },
      {
        Header: t('SELL_BY_PROD_T.MARGIN_LABEL'),
        accessor: 'margen',
        Footer:
          seeMargenProd && footerValues.margen ? (
            <FooterTextValue
              value={footerValues.margen.value}
              type={t(footerValues.margen.key)}
              seeMargenProd={seeMargenProd}
              rounded
            />
          ) : (
            <></>
          ),
      },
      {
        Header: t('SELL_BY_PROD_T.ACCIONES'),
        accessor: 'acciones',
        Footer: footerValues.acciones ? (
          <FooterTextValue
            value={footerValues.acciones.value}
            type={t(footerValues.acciones.key)}
            seeMargenProd={seeMargenProd}
            rounded
          />
        ) : (
          <></>
        ),
      },
    ]

    // Filtrar columnas según permisos y opciones de omisión
    let filteredColumns = [...allColumns]

    // Omitir columnas según permisos
    if (!seeCostProd) {
      filteredColumns = filteredColumns.filter(col => col.accessor !== 'costo')
    }

    if (!seeMargenProd) {
      filteredColumns = filteredColumns.filter(col => col.accessor !== 'margen')
    }

    // Omitir columnas según parámetro recibido
    if (omitColumns && omitColumns.length > 0) {
      filteredColumns = filteredColumns.filter(
        col => !omitColumns.includes(col.accessor)
      )
    }

    setCartColumns(filteredColumns)
  }

  useEffect(() => {
    getCartColumns()
  }, [cart, isShoppingCart])

  return { columns: cartColumns }
}
